import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  X, Send, ShieldCheck, ArrowUpRight, RotateCcw, Sparkles,
} from "lucide-react";
import { FLOWS, QUICK_ACTIONS } from "../lib/flow";
import { LINKS, API } from "../config";

const uid = (() => { let n = 0; return () => `m${++n}_${Date.now()}`; })();

const ROUTES = {
  business: {
    title: "Business Capital Profile",
    desc: "Complete your onboarding so an AFARI advisor can review your business funding options.",
    cta: "Start Capital Profile",
    href: LINKS.business,
    ncnd: true,
  },
  real_estate: {
    title: "Real Estate Financing Application",
    desc: "Submit your financing application and our team will assess your scenario.",
    cta: "Start Application",
    href: LINKS.realEstate,
    ncnd: true,
  },
  advisor: {
    title: "Book an AFARI Advisor",
    desc: "Pick a time that works for you and speak directly with a funding specialist.",
    cta: "Book a Call",
    href: LINKS.advisor,
    ncnd: false,
  },
};

const TypingDots = () => (
  <div className="flex items-center gap-1.5 px-1" aria-label="ARIA is typing...">
    {[0, 1, 2].map((i) => (
      <span key={i} className="w-2 h-2 rounded-full bg-[#94A3B8]"
        style={{ animation: "dot-bounce 1.2s infinite", animationDelay: `${i * 0.18}s` }} />
    ))}
  </div>
);

const Bubble = ({ role, children }) => {
  const isAria = role === "aria";
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28 }}
      className={`flex ${isAria ? "justify-start" : "justify-end"}`}>
      <div
        data-testid={`msg-${role}`}
        className={`max-w-[82%] px-4 py-2.5 text-[14px] leading-relaxed ${
          isAria
            ? "bg-[#F1F3F7] text-[#1E293B] rounded-2xl rounded-tl-md"
            : "bg-[#0B0B0D] text-white rounded-2xl rounded-tr-md"
        }`}>
        {children}
      </div>
    </motion.div>
  );
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [typing, setTyping] = useState(false);
  const [input, setInput] = useState("");
  const [mode, setMode] = useState("welcome"); // welcome | flow | general | done
  const [flowKey, setFlowKey] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [showForm, setShowForm] = useState(false);
  const [pendingRoute, setPendingRoute] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showTeaser, setShowTeaser] = useState(false);
  const sessionId = useRef(uid());
  const scrollRef = useRef(null);

  const scrollDown = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  }, []);

  useEffect(scrollDown, [messages, typing, showForm, pendingRoute, scrollDown]);

  // Greeting teaser: pop up a few seconds after load (once per session).
  useEffect(() => {
    if (sessionStorage.getItem("aria_teaser_seen")) return;
    const t = setTimeout(() => setShowTeaser((s) => (open ? s : true)), 4000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismissTeaser = () => {
    setShowTeaser(false);
    sessionStorage.setItem("aria_teaser_seen", "1");
  };

  // When embedded in an iframe, tell the host loader how large to size the frame.
  useEffect(() => {
    if (window.self === window.top) return;
    const type = open ? "aria:open" : showTeaser ? "aria:peek" : "aria:close";
    window.parent.postMessage({ type }, "*");
  }, [open, showTeaser]);

  const push = (role, content, extra = {}) =>
    setMessages((m) => [...m, { id: uid(), role, content, ...extra }]);

  const ariaSay = (content, extra = {}) =>
    new Promise((res) => {
      setTyping(true);
      setTimeout(() => {
        setTyping(false);
        push("aria", content, extra);
        res();
      }, 650);
    });

  const greet = useCallback(async () => {
    if (messages.length) return;
    await ariaSay(
      "Hello, I'm ARIA — AFARI's funding concierge. I can answer questions and help you find the right path. What brings you in today?",
      { actions: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length]);

  useEffect(() => { if (open) greet(); }, [open, greet]);

  const resetChat = () => {
    setMessages([]); setMode("welcome"); setFlowKey(null); setStepIdx(0);
    setAnswers({}); setForm({ name: "", email: "", phone: "" });
    setShowForm(false); setPendingRoute(null);
    sessionId.current = uid();
    setTyping(false);
    setTimeout(() => ariaSay(
      "Fresh start. What can I help you explore — Business Funding, Real Estate Funding, a general question, or a chat with an advisor?",
      { actions: true }), 200);
  };

  // ---- quick actions ----
  const handleAction = async (id) => {
    const map = { business: "Business Funding", real_estate: "Real Estate Funding", general: "General Question", advisor: "Speak With an Advisor" };
    push("user", map[id]);

    if (id === "general") {
      setMode("general");
      await ariaSay("Of course — ask me anything about business funding or real estate financing and I'll do my best. What would you like to know?");
      return;
    }
    if (id === "advisor") {
      setMode("flow"); setFlowKey("advisor");
      await ariaSay("Wonderful — our advisors love these conversations. Let me grab a few details so we can follow up.");
      setShowForm(true);
      setPendingRoute("advisor");
      return;
    }
    // business or real_estate scripted flow
    setMode("flow"); setFlowKey(id); setStepIdx(0);
    const first = FLOWS[id].steps[0];
    await ariaSay(first.question, { choices: first.choices, stepKey: first.key });
  };

  // ---- scripted choice ----
  const handleChoice = async (stepKey, choice) => {
    push("user", choice);
    const next = { ...answers, [stepKey]: choice };
    setAnswers(next);
    setMessages((m) => m.map((x) => (x.stepKey === stepKey ? { ...x, choices: null } : x)));

    const flow = FLOWS[flowKey];
    const idx = flow.steps.findIndex((s) => s.key === stepKey);
    const nextStep = flow.steps[idx + 1];
    setStepIdx(idx + 1);

    if (nextStep) {
      await ariaSay(nextStep.question, { choices: nextStep.choices, stepKey: nextStep.key });
    } else {
      await ariaSay("Thank you — that's really helpful. To connect you with the right specialist, may I get your contact details?");
      setShowForm(true);
      setPendingRoute(flowKey);
    }
  };

  // ---- general AI chat ----
  const sendGeneral = async () => {
    const text = input.trim();
    if (!text) return;
    push("user", text);
    setInput("");
    setTyping(true);
    try {
      const { data } = await axios.post(`${API}/chat`, { session_id: sessionId.current, message: text });
      setTyping(false);
      push("aria", data.reply);
      if (data.escalate) {
        await ariaSay("Would you like to speak with an AFARI advisor about this?");
        setPendingRoute("advisor");
        setMessages((m) => [...m, { id: uid(), role: "aria", content: "", route: "advisor" }]);
      }
    } catch (e) {
      setTyping(false);
      push("aria", "I'm having trouble reaching my knowledge base right now. You can always book an advisor and we'll help you directly.");
      setMessages((m) => [...m, { id: uid(), role: "aria", content: "", route: "advisor" }]);
    }
  };

  // ---- lead capture ----
  const submitLead = async () => {
    if (!form.name.trim() || !form.email.trim()) return;
    setSubmitting(true);
    const transcript = messages
      .filter((m) => m.content && (m.role === "aria" || m.role === "user"))
      .map((m) => ({ role: m.role, content: m.content }));
    try {
      await axios.post(`${API}/leads`, {
        pathway: pendingRoute,
        name: form.name, email: form.email, phone: form.phone,
        answers,
        transcript,
        routed_to: pendingRoute,
      });
    } catch (e) { console.error("Lead submission failed", e); /* still route the user */ }
    setSubmitting(false);
    setShowForm(false);
    push("user", `${form.name} · ${form.email}${form.phone ? " · " + form.phone : ""}`);
    await ariaSay(
      pendingRoute === "advisor"
        ? "Perfect. Here's the fastest way to lock in time with an advisor:"
        : "You're all set. Based on what you shared, here's your recommended next step:",
    );
    setMessages((m) => [...m, { id: uid(), role: "aria", content: "", route: pendingRoute }]);
    setMode("done");
  };

  const RouteCard = ({ routeKey }) => {
    const r = ROUTES[routeKey];
    if (!r) return null;
    return (
      <div className="w-full" data-testid={`route-card-${routeKey}`}>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_10px_30px_rgba(10,17,40,0.10)]">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={15} className="text-[#8E1B22]" strokeWidth={1.8} />
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#8E1B22]">Recommended</p>
          </div>
          <h4 className="font-display text-lg text-[#0B0B0D] font-bold">{r.title}</h4>
          <p className="text-[13px] text-[#64748B] leading-relaxed mt-1 mb-3">{r.desc}</p>
          <a href={r.href} target="_blank" rel="noopener noreferrer"
            data-testid={`route-cta-${routeKey}`}
            className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0B0B0D] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#17171A]">
            {r.cta}
            <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          {r.ncnd && (
            <a href={LINKS.ncnd} target="_blank" rel="noopener noreferrer"
              data-testid={`ncnd-link-${routeKey}`}
              className="mt-2 block text-center text-[12px] text-[#64748B] underline underline-offset-2 hover:text-[#0B0B0D]">
              Review the Mutual NCND Agreement
            </a>
          )}
        </div>
      </div>
    );
  };

  const flowActive = mode === "flow" && flowKey && flowKey !== "advisor";
  const progress = flowActive ? Math.min(stepIdx / FLOWS[flowKey].steps.length, 1) : 0;
  const showComposer = mode === "general";

  return (
    <>
      {/* Launcher */}
      <AnimatePresence>
        {!open && (
          <motion.button
            data-testid="chat-launcher"
            initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0, opacity: 0 }}
            onClick={() => setOpen(true)}
            className="aria-breathe fixed bottom-6 right-6 z-50 flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-[#0B0B0D] ring-1 ring-[#C0392B]/40"
            aria-label="Open ARIA chat">
            <img src="/afari-logo.png" alt="AFARI" className="h-16 w-16 scale-[1.7] object-cover" style={{ objectPosition: "50% 26%" }} />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Greeting teaser */}
      <AnimatePresence>
        {!open && showTeaser && (
          <motion.div
            data-testid="teaser-bubble"
            initial={{ opacity: 0, y: 12, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.96 }} transition={{ duration: 0.3 }}
            className="fixed bottom-[92px] right-6 z-50 w-[248px] rounded-2xl border border-white/10 bg-[#0B0B0D] p-4 shadow-[0_16px_40px_rgba(0,0,0,0.4)]">
            <button data-testid="teaser-dismiss" onClick={dismissTeaser} aria-label="Dismiss"
              className="absolute right-2 top-2 rounded-full p-1 text-[#94A3B8] transition-colors hover:bg-white/10 hover:text-white">
              <X size={14} strokeWidth={2} />
            </button>
            <p className="pr-4 font-display text-[15px] font-bold leading-snug text-white">
              Questions about funding?
            </p>
            <p className="mt-1 text-[12px] leading-relaxed text-[#94A3B8]">
              I'm ARIA — ask me anything or find your funding path in a minute.
            </p>
            <button data-testid="teaser-open"
              onClick={() => { dismissTeaser(); setOpen(true); }}
              className="mt-3 w-full rounded-full bg-[#C0392B] px-4 py-2 text-[13px] font-bold text-white transition-transform hover:scale-[1.02]">
              Chat with ARIA
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-window"
            initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }} transition={{ duration: 0.25 }}
            className="fixed z-50 flex flex-col overflow-hidden bg-white shadow-[0_24px_60px_rgba(0,0,0,0.35)]
                       inset-0 sm:inset-auto sm:bottom-6 sm:right-6 sm:h-[640px] sm:max-h-[85vh] sm:w-[400px] sm:rounded-3xl sm:border sm:border-white/20">
            {/* progress line */}
            <div className="h-[3px] w-full bg-[#0B0B0D]">
              <motion.div className="h-full bg-[#C0392B]" animate={{ width: `${progress * 100}%` }} transition={{ duration: 0.4 }} />
            </div>

            {/* Header */}
            <div className="relative flex items-center justify-between bg-[#0B0B0D] px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full border border-[#C0392B]/50 bg-[#17171A]">
                  <span className="font-display text-base font-bold text-[#C0392B]">A</span>
                </div>
                <div>
                  <p className="font-display text-[17px] font-bold leading-none text-white">ARIA</p>
                  <p className="mt-1 text-[11px] tracking-wide text-[#94A3B8]">AFARI Funding Concierge</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button data-testid="chat-reset" onClick={resetChat} title="Start over"
                  className="rounded-full p-2 text-[#94A3B8] transition-colors hover:bg-white/10 hover:text-white">
                  <RotateCcw size={17} strokeWidth={1.8} />
                </button>
                <button data-testid="chat-close" onClick={() => setOpen(false)} title="Close"
                  className="rounded-full p-2 text-[#94A3B8] transition-colors hover:bg-white/10 hover:text-white">
                  <X size={19} strokeWidth={1.8} />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="custom-scroll flex-1 space-y-3 overflow-y-auto bg-white px-4 py-5" aria-live="polite">
              {messages.map((m) => (
                <div key={m.id} className="space-y-3">
                  {m.content && <Bubble role={m.role}>{m.content}</Bubble>}

                  {m.actions && (
                    <div className="grid grid-cols-1 gap-2 pt-1">
                      {QUICK_ACTIONS.map((a) => (
                        <button key={a.id} data-testid={`action-${a.id}`}
                          onClick={() => handleAction(a.id)}
                          className="group flex items-center justify-between rounded-full border border-slate-200 bg-white px-4 py-2.5 text-left text-[14px] font-medium text-[#1E293B] transition-all hover:scale-[1.02] hover:border-[#C0392B]">
                          {a.label}
                          <ArrowUpRight size={15} className="text-[#8E1B22] opacity-0 transition-opacity group-hover:opacity-100" />
                        </button>
                      ))}
                    </div>
                  )}

                  {m.choices && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {m.choices.map((c) => (
                        <button key={c} data-testid={`choice-${m.stepKey}-${c}`}
                          onClick={() => handleChoice(m.stepKey, c)}
                          className="rounded-full border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-medium text-[#1E293B] transition-all hover:scale-[1.02] hover:border-[#C0392B]">
                          {c}
                        </button>
                      ))}
                    </div>
                  )}

                  {m.route && <RouteCard routeKey={m.route} />}
                </div>
              ))}

              {typing && (
                <div className="flex justify-start">
                  <div className="rounded-2xl rounded-tl-md bg-[#F1F3F7] px-4 py-3"><TypingDots /></div>
                </div>
              )}

              {/* Lead capture form */}
              {showForm && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  data-testid="lead-form"
                  className="rounded-2xl border border-slate-200 bg-[#F8F9FA] p-4">
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#8E1B22]">Your Details</p>
                  <div className="space-y-2.5">
                    <input data-testid="lead-name" value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Full name"
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-[14px] outline-none focus:ring-2 focus:ring-[#C0392B]" />
                    <input data-testid="lead-email" type="email" value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="Email address"
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-[14px] outline-none focus:ring-2 focus:ring-[#C0392B]" />
                    <input data-testid="lead-phone" value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="Phone (optional)"
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-[14px] outline-none focus:ring-2 focus:ring-[#C0392B]" />
                    <button data-testid="lead-submit" onClick={submitLead} disabled={submitting}
                      className="w-full rounded-full bg-[#0B0B0D] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#17171A] disabled:opacity-60">
                      {submitting ? "Submitting…" : "Continue"}
                    </button>
                  </div>
                  <p className="mt-2 flex items-center gap-1.5 text-[11px] text-[#64748B]">
                    <ShieldCheck size={13} strokeWidth={1.8} /> Your information is kept secure and private.
                  </p>
                </motion.div>
              )}
            </div>

            {/* Composer (general mode only) */}
            {showComposer && (
              <div className="border-t border-slate-100 bg-white px-3 py-3">
                <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-[#F8F9FA] px-3 py-1.5">
                  <input data-testid="chat-input" value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendGeneral()}
                    placeholder="Ask ARIA a question…"
                    className="flex-1 bg-transparent px-1 py-1.5 text-[14px] outline-none" />
                  <button data-testid="chat-send" onClick={sendGeneral}
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0B0B0D] text-[#C0392B] transition-colors hover:bg-[#17171A]">
                    <Send size={16} strokeWidth={1.8} />
                  </button>
                </div>
              </div>
            )}

            {/* Disclaimer footer */}
            <div className="border-t border-slate-100 bg-white px-4 py-2">
              <p className="text-center text-[10px] leading-tight text-[#94A3B8]">
                ARIA provides general information only — no approval, rate, amount, or timeline is guaranteed.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
